import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import {
  useUserPreferences,
  useAcknowledgeAnalyticsNotice,
} from '@/hooks/queries'

/**
 * KAN-32 F1 — the in-app analytics notice.
 *
 * WHAT IT IS. A notice, not a consent gate. P1 ratified "no consent banner",
 * and the analytics is cookieless with `persistence: 'memory'`, so there is
 * nothing to accept or reject. This tells the user what is collected, states
 * the Art. 21 objection route, and gets out of the way. There is exactly one
 * button and it means "I have read this".
 *
 * WHY IT EXISTS AT ALL. The LIA (§10.2) reads Art. 21(4) — the right to object
 * must be brought to the subject's attention explicitly, at the latest at the
 * time of first communication, clearly and separately from other information —
 * and concludes that the invitation Operator Note does not satisfy it on its
 * own. It names this notice, plus a standing control in Settings, plus the
 * Privacy Notice statement, as what does. So this is a compliance surface.
 *
 * WHY IT IS IN THE FLOW AND NOT A MODAL OR A CORNER TOAST. "Clearly and
 * separately" is the Art. 21(4) wording. A modal would block the product and
 * would read as a consent gate — the thing P1 forbids. A toast is dismissible
 * by inattention. An in-flow region at the top of the content area is
 * prominent, non-blocking, and reachable by keyboard in document order.
 *
 * WHY THE DISMISSAL IS SERVER-PERSISTED. P1 forbids new client-side storage,
 * and the Privacy Notice draft commits publicly to writing no cookie and no
 * local storage. A localStorage flag would contradict the published claim and
 * would re-notify the same person on every new browser. It rides on
 * `user_preferences.analytics_notice_seen_at` (migration 0099), the same place
 * the replay objection lives.
 *
 * WHY IT READS AND WRITES THROUGH ONE QUERY KEY. The Art. 21 opt-out stored
 * its value under one React Query key and enforced it from another, so an
 * objection took effect only after a page reload (error log 2026-07-26). This
 * component reads `useUserPreferences` and acknowledges through
 * `useAcknowledgeAnalyticsNotice`, which invalidates that exact key. Dismissal
 * therefore takes effect immediately AND survives a reload, because the
 * timestamp is on the server.
 *
 * WHY IT IS GATED ON THE ANALYTICS KEY. Production runs with no
 * `VITE_POSTHOG_KEY`, so no analytics is collected. A notice describing
 * collection that is not happening is its own accuracy defect — the Privacy
 * Notice amendment (Part A.3) makes exactly that point about publishing the
 * analytics sections early. The notice appears in the same build in which
 * analytics is live, and not before.
 *
 * COPY PROVENANCE. Derived from the drafted Privacy Policy revision §3.9/§3.10
 * and the Privacy Notice amendment Part C.2/C.3, both at
 * `cyberfind-strategy/legal/drafts/`. The session-recording sentence describes
 * the CURRENT posture: decision O8 narrowed replay to a named, time-boxed
 * investigation on a subset of routes. Copy written from the superseded
 * P2/P3 posture ("on by default across the signed-in surface") would be wrong.
 * Requires Captain/counsel sign-off before production analytics enablement.
 */

// Same build-time input that `initAnalytics` reads in main.tsx. If analytics
// is not initialised there is nothing to disclose.
const ANALYTICS_ENABLED = Boolean(import.meta.env.VITE_POSTHOG_KEY)

export interface AnalyticsNoticeProps {
  /** Storybook/test escape hatch: render regardless of the build-time gate. */
  forceEnabled?: boolean
}

export function AnalyticsNotice({ forceEnabled }: AnalyticsNoticeProps = {}) {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences, isSuccess } = useUserPreferences(userId)
  const acknowledge = useAcknowledgeAnalyticsNotice()

  const enabled = forceEnabled || ANALYTICS_ENABLED
  // Every condition must be affirmatively true. An unresolved session, an
  // unresolved preferences read, or an errored read all render nothing —
  // showing a notice whose acknowledgement cannot be recorded would produce a
  // banner that reappears forever.
  const shouldShow =
    enabled &&
    Boolean(userId) &&
    isSuccess &&
    !preferences?.analyticsNoticeSeenAt

  if (!shouldShow) return null

  return (
    <section
      aria-labelledby="analytics-notice-heading"
      data-testid="analytics-notice"
      className="mx-4 mt-4 md:mx-6 rounded-lg border border-border bg-muted/40 p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h2
            id="analytics-notice-heading"
            className="text-sm font-semibold"
          >
            How we measure use of IntelHub
          </h2>
          <p className="text-xs text-muted-foreground">
            While you are signed in, we record which pages and features you
            use, so we can fix what is confusing and build what is used. We
            collect the shape of what you did, not the content of what you read
            or typed — never your search text, story titles, email address or
            company name.
          </p>
          <p className="text-xs text-muted-foreground">
            Nothing is stored on your device. The analytics library keeps its
            state in memory only, so it writes no cookie and no local storage.
          </p>
          <p className="text-xs text-muted-foreground">
            Session recording is off by default. We switch it on only for a
            named, time-boxed investigation, on a limited set of pages. Every
            input and all on-screen text are masked before a recording leaves
            your browser, and operator screens are never recorded.
          </p>
          <p className="text-xs text-muted-foreground">
            You can object at any time in{' '}
            <Link
              to="/settings#settings-privacy"
              className="underline underline-offset-2"
            >
              Settings → Privacy
            </Link>
            . You do not need to explain yourself, and it does not affect your
            access to any part of IntelHub. Full detail is in our{' '}
            <Link to="/privacy" className="underline underline-offset-2">
              Privacy Notice
            </Link>
            .
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          disabled={acknowledge.isPending}
          onClick={() => acknowledge.mutate({ userId })}
        >
          <X className="mr-1 h-3.5 w-3.5" aria-hidden />
          Got it
        </Button>
      </div>
    </section>
  )
}
